/**
 * Mentions API client (Milestone 4-B, spec §3.9).
 *
 * Wraps gateway endpoints in apps/chaos-gateway/src/mentions.ts:
 *   GET  /api/orgs/:orgId/mentions
 *   POST /api/mentions/:id/seen
 *   POST /api/mentions/:id/acted-on
 */
import { apiJson } from './client';

/**
 * The full DB CHECK set for `chaos.mentions.source_kind` after migration 0082
 * (drop+recreate). Widened from the legacy 6-kind union so the inbox switch has
 * no un-typed cases: this now mirrors the gateway exactly (legacy parser kinds +
 * the dead UI kinds + the new inbox producers + the program-open kinds).
 */
export type MentionSourceKind =
  | 'card_note'
  | 'sub_task'
  | 'sub_task_comment'
  | 'handoff'
  | 'role_message'
  | 'session_message'
  | 'role_message_response'
  | 'artifact_caption'
  | 'session_ask'
  | 'session_run'
  | 'assignment'
  | 'needs_action'
  | 'notification';

/** Inbox lane (migration 0082). Legacy @-mentions have category=null. */
export type InboxCategory = 'needs_you' | 'notification';

/**
 * WHY a row exists — drives the intent glyph + lane. Finer than source_kind.
 * Legacy @-mention rows carry intent=null (rendered as a plain mention).
 */
export type InboxIntent =
  | 'mention'
  | 'ask'
  | 'approval'
  | 'blocker'
  | 'for_approval'
  | 'progress'
  | 'done'
  // migration 0104 — a blocked agent's access-request Needs-You item.
  | 'access_request';

export type MentionedKind = 'user' | 'role';
export type ActorKind = 'user' | 'role';

export interface Mention {
  id: string;
  orgId: string;
  sourceKind: MentionSourceKind;
  sourceId: string;
  cardId: string | null;
  mentionedKind: MentionedKind;
  mentionedUserId: string | null;
  mentionedRoleId: string | null;
  byKind: ActorKind;
  byUserId: string | null;
  byRoleId: string | null;
  contextBefore: string | null;
  contextAfter: string | null;
  notifiedAt: string | null;
  seenAt: string | null;
  actedOnAt: string | null;
  createdAt: string;
  // Inbox surface (migration 0082) — additive; null on legacy @-mention rows
  // (which the gateway folds into the Notifications lane).
  category: InboxCategory | null;
  intent: InboxIntent | null;
  title: string | null;
  bodyPreview: string | null;
  sessionId: string | null;
  askId: string | null;
  dismissedAt: string | null;
  // Scoped Knowledge Routing (spec CHAOS_KNOWLEDGE_ROUTING_ARCHITECTURE.md
  // §3.5) — set by the gateway mapper on `sourceKind='knowledge_addition'` rows
  // so the Inbox can deep-link to the right knowledge review section. `scope`
  // discriminates org vs project (undefined on all other rows); `targetId` is
  // the org id (org scope) or board/project id (project scope). Optional +
  // additive: absent on every non-knowledge row, so byte-identical elsewhere.
  knowledgeScope?: 'project' | 'org';
  knowledgeTargetId?: string;
}

export interface ListMentionsParams {
  forUserId?: string;
  forRoleId?: string;
  cardId?: string;
  unread?: boolean | null; // true = unread only, false = read only, null/undef = all
  limit?: number;
}

interface MentionsListResponse {
  mentions: Mention[];
}
interface MentionResponse {
  mention: Mention;
}

function buildQuery(params: ListMentionsParams = {}): string {
  const qs = new URLSearchParams();
  if (params.forUserId) qs.set('for_user_id', params.forUserId);
  if (params.forRoleId) qs.set('for_role_id', params.forRoleId);
  if (params.cardId) qs.set('card_id', params.cardId);
  if (params.unread === true) qs.set('unread', 'true');
  if (params.unread === false) qs.set('unread', 'false');
  if (params.limit) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const mentionsApi = {
  list(orgId: string, params: ListMentionsParams = {}): Promise<Mention[]> {
    return apiJson<MentionsListResponse>(`/orgs/${orgId}/mentions${buildQuery(params)}`).then(
      (r) => r.mentions,
    );
  },
  markSeen(id: string): Promise<Mention> {
    return apiJson<MentionResponse>(`/mentions/${id}/seen`, { method: 'POST' }).then(
      (r) => r.mention,
    );
  },
  markActedOn(id: string): Promise<Mention> {
    return apiJson<MentionResponse>(`/mentions/${id}/acted-on`, { method: 'POST' }).then(
      (r) => r.mention,
    );
  },
};

export const mentionsKeys = {
  all: ['mentions'] as const,
  list: (orgId: string, params: ListMentionsParams = {}) =>
    [...mentionsKeys.all, 'list', orgId, params] as const,
};
